import React, { useMemo, useState } from 'react';
import { Checkbox, Menu, Spin } from 'antd';
import type { CheckboxChangeEvent } from 'antd/es/checkbox';
import { PaginatedOptionsList } from './PaginatedOptionsList';
import { LocationRow } from '../../ControlTower/types';
import { ChevronDownIcon } from '@/assets/icons/icons';
import EllipsisTooltip from '@/components/EllipsisTooltip';
import '../FilterDropdowns.css';

export default function GroupedLocationsPaginatedList({
  rows,
  loading,
  height,
  onBottomReached,
  stopAll,
  selectedOptions,
  setSelectedOptions,
  isFromHome = false,
  hiddenOptionIds = new Set(),
  selectAllActive = false,
  onToggleSelectAll,
}: {
  rows: LocationRow[];
  loading: boolean;
  height: number;
  onBottomReached: () => void;
  stopAll: (e: React.MouseEvent) => void;
  selectedOptions: { id: string | number; name?: string }[];
  setSelectedOptions?: (next: unknown) => void;
  isFromHome?: boolean;
  hiddenOptionIds?: Set<string>;
  /** When true, every row (loaded or not-yet-loaded) renders as checked —
   *  lets "select all" survive pagination instead of only covering the ids
   *  loaded at the moment it was clicked. Opt-in; other callers are unaffected. */
  selectAllActive?: boolean;
  /** Called instead of mutating selectedOptions when the top checkbox is toggled,
   *  so the caller can track "select all" as its own flag. */
  onToggleSelectAll?: (checked: boolean) => void;
}) {
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});

  const visibleRows = useMemo(() => {
    return rows
      .map((r) => {
        if (r.type === 'single') return r;

        return {
          ...r,
          locations: r.locations.filter(
            (c) => !hiddenOptionIds.has(String(c.id))
          ),
        };
      })
      .filter((r) => {
        if (r.type === 'single') {
          return !hiddenOptionIds.has(String(r.id));
        }

        const hasChildren = (r.locations?.length || 0) > 0;
        const groupHidden = hiddenOptionIds.has(String(r.id));
        return hasChildren || !groupHidden;
      });
  }, [rows, hiddenOptionIds]);

  const selectedIdSet = useMemo(
    () => new Set(selectedOptions.map((s) => String(s.id))),
    [selectedOptions]
  );

  const leafItems = useMemo(() => {
    const out: { id: string; name: string }[] = [];
    for (const r of visibleRows) {
      if (r.type === 'single') out.push({ id: r.id, name: r.name });
      else r.locations.forEach((c) => out.push({ id: c.id, name: c.name }));
    }
    return out;
  }, [visibleRows]);

  const allLeafIds = useMemo(() => leafItems.map((x) => x.id), [leafItems]);

  const allChecked =
    selectAllActive ||
    (allLeafIds.length > 0 &&
      allLeafIds.every((id) => selectedIdSet.has(String(id))));

  const setSelectedByIds = (ids: { id: string; name: string }[]) => {
    setSelectedOptions?.(ids.map((x) => ({ id: x.id, name: x.name })));
  };

  const toggleLeaf = (checked: boolean, leaf: { id: string; name: string }) => {
    if (!setSelectedOptions) return;

    if (checked) {
      if (selectAllActive || selectedIdSet.has(String(leaf.id))) return;
      setSelectedOptions([
        ...selectedOptions,
        { id: leaf.id, name: leaf.name },
      ]);
      return;
    }

    if (selectAllActive) {
      // Selection was "all" — deselecting one item breaks that into an
      // explicit whitelist of everything else currently loaded.
      onToggleSelectAll?.(false);
      setSelectedOptions(
        leafItems.filter((x) => String(x.id) !== String(leaf.id))
      );
      return;
    }

    setSelectedOptions(
      selectedOptions.filter((s) => String(s.id) !== String(leaf.id))
    );
  };

  const toggleGroup = (
    checked: boolean,
    group: Extract<LocationRow, { type: 'group' }>
  ) => {
    if (!setSelectedOptions) return;

    const childIds = group.locations.map((c) => String(c.id));

    if (checked) {
      if (selectAllActive) return;
      const existing = new Set(selectedOptions.map((s) => String(s.id)));
      const add = group.locations
        .filter((c) => !existing.has(String(c.id)))
        .map((c) => ({ id: c.id, name: c.name }));
      setSelectedOptions([...selectedOptions, ...add]);
      return;
    }

    if (selectAllActive) {
      onToggleSelectAll?.(false);
      setSelectedOptions(
        leafItems.filter((x) => !childIds.includes(String(x.id)))
      );
      return;
    }

    setSelectedOptions(
      selectedOptions.filter((s) => !childIds.includes(String(s.id)))
    );
  };

  return (
    <PaginatedOptionsList
      enabled
      onBottomReached={onBottomReached}
      height={height}
      stopAll={stopAll}
      className='subfilter-paginated-scroll'
    >
      {leafItems.length > 0 && (
        <Menu.Item key='__select_all' className='gl-select-all-item'>
          <Checkbox
            className='custom-menu-shipment-checkbox-only gl-checkbox checkbox-sm font-12'
            checked={allChecked}
            onChange={(e: CheckboxChangeEvent) => {
              if (onToggleSelectAll) {
                // Parent owns selection (including clearing concrete ids).
                // Do not wipe selectedOptions here — Home needs to assign
                // visible leaf ids when Select all is used with an active search.
                onToggleSelectAll(e.target.checked);
                return;
              }
              if (e.target.checked) setSelectedByIds(leafItems);
              else setSelectedByIds([]);
            }}
          >
            Select all
          </Checkbox>
        </Menu.Item>
      )}

      {visibleRows.map((r, idx) => {
        if (r.type === 'single') {
          const checked = selectAllActive || selectedIdSet.has(String(r.id));
          return (
            <Menu.Item
              // eslint-disable-next-line react/no-array-index-key -- the same location id can appear on more than one page of results, so the id alone is not a unique key
              key={`s-${r.id}-${idx}`}
              className='gl-menu-item gl-single-item'
            >
              <Checkbox
                className='custom-menu-shipment-checkbox-only gl-checkbox checkbox-sm'
                checked={checked}
                onChange={(e) =>
                  toggleLeaf(e.target.checked, { id: r.id, name: r.name })
                }
              >
                <div className='gl-text-wrapper'>
                  <EllipsisTooltip text={r.name} className='gl-name' />
                  {r.address ? (
                    <EllipsisTooltip text={r.address} className='gl-address' />
                  ) : null}
                </div>
              </Checkbox>
            </Menu.Item>
          );
        }

        const groupKey = String(r.id);
        const open = !!openGroups[groupKey];

        const children = r.locations || [];
        const total = children.length;
        const selectedCount = children.filter((c) =>
          selectedIdSet.has(String(c.id))
        ).length;

        const checked =
          selectAllActive || (total > 0 && selectedCount === total);

        return (
          <React.Fragment key={`g-${groupKey}`}>
            <Menu.Item className='gl-menu-item gl-group-item'>
              <div className='gl-group-row'>
                <Checkbox
                  className='custom-menu-shipment-checkbox-only gl-checkbox  checkbox-sm'
                  checked={checked}
                  onChange={(e) => toggleGroup(e.target.checked, r)}
                >
                  <div className='gl-text-wrapper'>
                    <EllipsisTooltip
                      text={`${r.display_name} (${r.children_count ?? total})`}
                      className='gl-name'
                    />
                    {r.address ? (
                      <EllipsisTooltip
                        text={r.address}
                        className='gl-address'
                      />
                    ) : null}
                  </div>
                </Checkbox>

                <button
                  onClick={() =>
                    setOpenGroups((p) => ({ ...p, [groupKey]: !p[groupKey] }))
                  }
                  className='gl-chevron-btn'
                  aria-label={open ? 'Collapse' : 'Expand'}
                >
                  <ChevronDownIcon
                    size={isFromHome ? 16 : 24}
                    className={`chevron-icon ${open ? 'rotated' : ''}`}
                  />
                </button>
              </div>
            </Menu.Item>

            {open &&
              children.map((c) => {
                const cChecked =
                  selectAllActive || selectedIdSet.has(String(c.id));
                return (
                  <Menu.Item
                    key={`c-${c.id}`}
                    className='gl-menu-item gl-child-item'
                  >
                    <div className='gl-child-row'>
                      <Checkbox
                        className='custom-menu-shipment-checkbox-only gl-checkbox checkbox-sm'
                        checked={cChecked}
                        onChange={(e) =>
                          toggleLeaf(e.target.checked, {
                            id: c.id,
                            name: c.name,
                          })
                        }
                      >
                        <div className='gl-text-wrapper'>
                          <EllipsisTooltip text={c.name} className='gl-name' />
                        </div>
                      </Checkbox>
                    </div>
                  </Menu.Item>
                );
              })}
          </React.Fragment>
        );
      })}

      {loading && (
        <Menu.Item key='loading' disabled className='subfilter-loading-item'>
          <div className='subfilter-loading-inner'>
            <Spin size='small' />
          </div>
        </Menu.Item>
      )}
    </PaginatedOptionsList>
  );
}
